import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { IconType } from '../icon/icon.component';
import { UploadProgressOverlayService, UploadOverlayState, UploadTask } from '../../services/upload-progress-overlay.service';

@Component({
  selector: 'app-upload-progress-overlay',
  templateUrl: './upload-progress-overlay.component.html',
  styleUrls: ['./upload-progress-overlay.component.scss']
})
export class UploadProgressOverlayComponent implements OnInit, OnDestroy {
  state: UploadOverlayState = {
    visible: false,
    title: '',
    tasks: [],
    totalProgress: 0,
    canCancel: true
  };

  private destroy$ = new Subject<void>();

  constructor(
    public uploadService: UploadProgressOverlayService
  ) {}

  ngOnInit(): void {
    this.uploadService.getState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.state = state;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Get status icon for a task
   */
  getStatusIcon(status: UploadTask['status']): IconType {
    switch (status) {
      case 'completed':
        return 'check';
      case 'error':
        return 'danger';
      case 'cancelled':
        return 'close';
      case 'uploading':
        return 'upload';
      default:
        return 'clock';
    }
  }

  /**
   * Get status label for a task
   */
  getStatusLabel(task: UploadTask): string {
    switch (task.status) {
      case 'pending':
        return 'En attente';
      case 'uploading':
        return `${task.progress}%`;
      case 'completed':
        return 'Terminé';
      case 'error':
        return task.error || 'Erreur';
      case 'cancelled':
        return 'Annulé';
      default:
        return '';
    }
  }

  /**
   * Get CSS class for task status
   */
  getStatusClass(status: UploadTask['status']): string {
    return `task-${status}`;
  }

  /**
   * Check if there are any active uploads
   */
  hasActiveUploads(): boolean {
    return this.state.tasks.some(task =>
      task.status === 'uploading' || task.status === 'pending'
    );
  }

  /**
   * Handle cancel button click
   */
  onCancel(): void {
    if (confirm('Voulez-vous vraiment annuler les uploads en cours ?')) {
      this.uploadService.cancelAll();
      // Hide after a short delay to show cancelled status
      setTimeout(() => {
        this.uploadService.hide();
      }, 1500);
    }
  }

  /**
   * Handle close button click (only when complete)
   */
  onClose(): void {
    this.uploadService.hide();
  }

  /**
   * Get upload speed and ETA info
   */
  getUploadInfo(): string {
    const uploadingTasks = this.state.tasks.filter(t => t.status === 'uploading');
    if (uploadingTasks.length === 0) return '';

    const task = uploadingTasks[0]; // Show info for first uploading task
    const parts: string[] = [];

    if (task.uploaded && task.size) {
      parts.push(`${this.uploadService.formatSize(task.uploaded)} / ${this.uploadService.formatSize(task.size)}`);
    }

    if (task.speed) {
      parts.push(this.uploadService.formatSpeed(task.speed));
    }

    if (task.eta) {
      parts.push(`~${this.uploadService.formatETA(task.eta)}`);
    }

    return parts.join(' • ');
  }
}
